<template>
    <card-normal class="user-team-card">
        <template #sections>
            <card-section area="card-header" class="user-team-card__header">
                <text-decorator bold>
                    <slot name="title" />
                </text-decorator>

                <template v-if="props.forwardable">
                    <button
                        type="button"
                        class="user-team-card__forward-button"
                        @click="redirectToForward"
                    >
                        <svg-icon
                            :icon="ReplyIcon"
                        />
                    </button>
                </template>
            </card-section>
        </template>
        <flex-container :gap="Size.sm">
            <div v-if="!props.editable">
                <user-team-block
                    :user="props.user"
                    :team="props.team"
                />
            </div>
            <input-group
                v-else
                v-model="currentUser"
            >
                <template #input>
                    <select-input
                        :items="orderedUsers"
                        value-key-name="id"
                        label-key-name="name"
                        variant="inline"
                        arrow-variant="unfold"
                    >
                        <template #active>
                            <user-team-block
                                :user="currentUser"
                                :team="props.team"
                            />
                        </template>
                        <template #item="{ bindings }">
                            <span style="display:flex; align-items: center;">
                                <user-avatar
                                    :placeholder="bindings.item.name.abbreviation"
                                    size="md"
                                    :url="bindings.item.avatar ? resizedToSizeUploadUrl(bindings.item.avatar, 'md') : ''"
                                    :alt="bindings.item.name"
                                />
                                <span>&nbsp;{{ bindings.item.name.fullName }}</span>
                            </span>
                        </template>
                    </select-input>
                </template>
            </input-group>
            <transition name="fade">
                <flex-container
                    v-if="hasChanges"
                    :gap="Size.sm"
                    justify-content="flex-end"
                >
                    <button-outline @click="cancel">
                        Annuleren
                    </button-outline>
                    <button-block @click="submit">
                        Opslaan
                    </button-block>
                </flex-container>
            </transition>
        </flex-container>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        CardNormal,
        CardSection,
        TextDecorator,
        SelectInput,
        InputGroup,
        UserAvatar,
        Size,
        ButtonOutline,
        FlexContainer,
        ButtonBlock,
    } from '@keyvue/one-ui';

    import UserTeamBlock from '@components/Blocks/UserTeamBlock.vue';
    import ReplyIcon from '@keyvue/one-material-icons/icons/reply.svg?component';

    import type { User, Team, Lead } from '@keyvue/one-api-client';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import { computed, type Ref, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import useParentRoute from '@composables/useParentRoute';

    export interface Props {
        lead: Lead
        user: User | null
        team: Team | null
        editable?: boolean
        forwardable?: boolean
        assignableUsers?: User[]
    }

    const router = useRouter();
    const createRouteLocation = useParentRoute();
    const emit = defineEmits(['user-change']);
    const props = defineProps<Props>();
    const currentUser = ref<User | null>(props.user) as Ref<User | null>;
    const hasChanges = ref(false);

    const submit = () => {
        emit('user-change', currentUser.value);
        hasChanges.value = false;
    };

    const cancel = () => {
        currentUser.value = props.user;
        hasChanges.value = false;
    };

    const redirectToForward = () => {
        router.push(createRouteLocation({
            name: 'ForwardLead',
            params: {
                leadId: props.lead?.id.value,
            },
        }, 'contact'));
    };

    const orderedUsers = computed(() => (
        [...(!props.assignableUsers ? [] : props.assignableUsers)].sort((a, b) => a.name.fullName.localeCompare(b.name.fullName))
    ));

    watch(currentUser, () => {
        hasChanges.value = currentUser.value?.id.id !== props.user?.id.value;
    });

    watch(props, () => {
        currentUser.value = props.user;
    });
</script>

<style lang="scss" scoped>
.user-team-card {
    &__header {
        position: relative;
    }

    &__forward-button {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: size(lg);
        padding: 0 size(sm);
        display: flex;
        border: 0;
        box-sizing: content-box;
        align-items: center;
        justify-content: center;
        transform: rotateY(180deg);
        color: color(brand-green);
        background-color: transparent;
        transition: background-color .2s, padding .2s, width .2s;
        cursor: pointer;
        border-top-left-radius: size(xs);

        &:hover {
            width: size(md);
            padding: 0 (size(md) - size(xs));
            background-color: color(gray-200);
        }
    }
}
</style>
